// Company Admin Impersonation Service
// Handles company admin switching between company admin and company user contexts
// Access controlled by 'company_admin' role
// Similar to tenant admin impersonation but with company-level isolation

import axiosInstance from '../config/axios.config';

class CompanyImpersonationService {
    constructor() {
        this.listeners = [];
    }

    /**
     * Start impersonating a company user
     * Company admins (with 'company_admin' role) can only impersonate company_user within their own company
     */
    async startImpersonation(userId) {
        try {
            console.log('=== COMPANY IMPERSONATION START ===');
            console.log('Target company user ID:', userId);

            // Store current company admin context before impersonation
            const currentAuth = {
                token: localStorage.getItem('authToken'),
                user: localStorage.getItem('user'),
                isAuthenticated: localStorage.getItem('isAuthenticated'),
                userRoles: localStorage.getItem('userRoles'),
            };

            console.log('Current company admin context stored');

            // Request impersonation from backend (company-specific endpoint)
            const response = await axiosInstance.post(`/company/impersonate/${userId}`);

            if (response.data.success) {
                // Store original company admin data for restoration
                localStorage.setItem('originalCompanyAdminToken', currentAuth.token);
                localStorage.setItem('originalCompanyAdminUser', currentAuth.user);
                localStorage.setItem('originalCompanyAdminAuth', currentAuth.isAuthenticated);
                localStorage.setItem('originalCompanyAdminRoles', currentAuth.userRoles || '[]');

                // Switch to impersonated company user context
                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('isCompanyImpersonating', 'true');
                localStorage.setItem('companyImpersonatedUserId', response.data.user.id);
                localStorage.setItem('companyImpersonatedUserName', response.data.user.name);

                // Update userRoles so the menu reflects the impersonated user's role
                if (response.data.roles) {
                    localStorage.setItem('userRoles', JSON.stringify(response.data.roles));
                }

                // Store original company admin info for display
                if (response.data.original_admin) {
                    localStorage.setItem('originalCompanyAdminInfo', JSON.stringify(response.data.original_admin));
                }

                console.log('Switched to impersonated company user:', response.data.user.name);
                console.log('Company ID:', response.data.user.company_id);
                console.log('==============================');

                // Notify all listeners about auth state change
                this.notifyAuthStateChange();

                return {
                    success: true,
                    user: response.data.user,
                    message: response.data.message
                };
            } else {
                throw new Error(response.data.message || 'Failed to start company impersonation');
            }

        } catch (error) {
            console.error('Company impersonation failed:', error);

            const errorMessage = error.response?.data?.message ||
                               error.message ||
                               'Failed to start company impersonation';

            throw new Error(errorMessage);
        }
    }

    /**
     * Stop impersonating and return to company admin context
     */
    async stopImpersonation() {
        try {
            console.log('=== COMPANY IMPERSONATION STOP ===');

            // Call backend to log impersonation end
            try {
                await axiosInstance.post('/company/stop-impersonation');
            } catch (error) {
                console.warn('Failed to notify backend about company impersonation stop:', error);
                // Continue with local cleanup even if backend call fails
            }

            // Restore original company admin context
            const originalToken = localStorage.getItem('originalCompanyAdminToken');
            const originalUser = localStorage.getItem('originalCompanyAdminUser');
            const originalAuth = localStorage.getItem('originalCompanyAdminAuth');
            const originalRoles = localStorage.getItem('originalCompanyAdminRoles');

            if (originalToken && originalUser) {
                localStorage.setItem('authToken', originalToken);
                localStorage.setItem('user', originalUser);
                localStorage.setItem('isAuthenticated', originalAuth || 'true');
                if (originalRoles) {
                    localStorage.setItem('userRoles', originalRoles);
                }

                // Clean up company impersonation data
                this.clearCompanyImpersonationData();

                console.log('Restored to company admin context');
                console.log('==================================');

                // Notify all listeners about auth state change
                this.notifyAuthStateChange();

                return { success: true, message: 'Returned to company admin view' };
            } else {
                throw new Error('Could not find original company admin context');
            }

        } catch (error) {
            console.error('Stop company impersonation failed:', error);

            // Fallback: try to clear impersonation data anyway
            this.clearCompanyImpersonationData();

            throw new Error(error.message || 'Failed to stop company impersonation');
        }
    }

    /**
     * Check if currently impersonating a company user
     */
    isImpersonating() {
        return localStorage.getItem('isCompanyImpersonating') === 'true';
    }

    /**
     * Get impersonated company user info
     */
    getImpersonatedUser() {
        if (!this.isImpersonating()) {
            return null;
        }

        return {
            id: localStorage.getItem('companyImpersonatedUserId'),
            name: localStorage.getItem('companyImpersonatedUserName'),
            user: JSON.parse(localStorage.getItem('user') || '{}')
        };
    }

    /**
     * Get original company admin user info
     */
    getOriginalCompanyAdmin() {
        const adminInfo = localStorage.getItem('originalCompanyAdminInfo');
        return adminInfo ? JSON.parse(adminInfo) : null;
    }

    /**
     * Get list of company users that can be impersonated
     * Only returns company_user from the same company
     * Supports pagination and search
     *
     * @param {Object} params - Query parameters
     * @param {number} params.page - Current page (1-based)
     * @param {number} params.per_page - Items per page
     * @param {string} params.search - Search query
     * @param {string} params.sort_field - Field to sort by
     * @param {string} params.sort_order - Sort order (asc/desc)
     */
    async getImpersonatableUsers(params = {}) {
        try {
            const queryParams = new URLSearchParams();

            if (params.page) queryParams.append('page', params.page);
            if (params.per_page) queryParams.append('per_page', params.per_page);
            if (params.search) queryParams.append('search', params.search);
            if (params.sort_field) queryParams.append('sort_field', params.sort_field);
            if (params.sort_order) queryParams.append('sort_order', params.sort_order);

            const queryString = queryParams.toString();
            const url = `/company/impersonatable-users${queryString ? `?${queryString}` : ''}`;

            const response = await axiosInstance.get(url);

            if (response.data.success) {
                return {
                    success: true,
                    users: response.data.users,
                    company: response.data.company,
                    pagination: response.data.pagination
                };
            } else {
                throw new Error(response.data.message || 'Failed to fetch company users');
            }

        } catch (error) {
            console.error('Failed to get impersonatable company users:', error);

            const errorMessage = error.response?.data?.message ||
                               error.message ||
                               'Failed to fetch company users';

            throw new Error(errorMessage);
        }
    }

    /**
     * Clear all company impersonation related data
     */
    clearCompanyImpersonationData() {
        localStorage.removeItem('isCompanyImpersonating');
        localStorage.removeItem('companyImpersonatedUserId');
        localStorage.removeItem('companyImpersonatedUserName');
        localStorage.removeItem('originalCompanyAdminToken');
        localStorage.removeItem('originalCompanyAdminUser');
        localStorage.removeItem('originalCompanyAdminAuth');
        localStorage.removeItem('originalCompanyAdminRoles');
        localStorage.removeItem('originalCompanyAdminInfo');
    }

    /**
     * Subscribe to auth state changes
     */
    onAuthStateChange(callback) {
        this.listeners.push(callback);

        // Return unsubscribe function
        return () => {
            this.listeners = this.listeners.filter(listener => listener !== callback);
        };
    }

    /**
     * Notify all listeners about auth state change
     */
    notifyAuthStateChange() {
        this.listeners.forEach(callback => {
            try {
                callback({
                    isCompanyImpersonating: this.isImpersonating(),
                    impersonatedUser: this.getImpersonatedUser(),
                    originalCompanyAdmin: this.getOriginalCompanyAdmin()
                });
            } catch (error) {
                console.error('Error in company auth state change listener:', error);
            }
        });
    }

    /**
     * Emergency cleanup - use if company impersonation gets stuck
     */
    emergencyStopImpersonation() {
        console.warn('Emergency company impersonation stop initiated');

        try {
            // Restore original company admin context if available
            const originalToken = localStorage.getItem('originalCompanyAdminToken');
            const originalUser = localStorage.getItem('originalCompanyAdminUser');
            const originalRoles = localStorage.getItem('originalCompanyAdminRoles');

            if (originalToken && originalUser) {
                localStorage.setItem('authToken', originalToken);
                localStorage.setItem('user', originalUser);
                localStorage.setItem('isAuthenticated', 'true');
                if (originalRoles) {
                    localStorage.setItem('userRoles', originalRoles);
                }
            }

            // Clear all company impersonation data
            this.clearCompanyImpersonationData();

            // Notify listeners
            this.notifyAuthStateChange();

            console.log('Emergency company impersonation cleanup completed');
            return { success: true, message: 'Emergency cleanup completed' };

        } catch (error) {
            console.error('Emergency company impersonation cleanup failed:', error);
            return { success: false, message: 'Emergency cleanup failed' };
        }
    }

    /**
     * Check if current user is a company admin (can use impersonation)
     * Checks for 'company_admin' role instead of user_type
     */
    isCompanyOwner() {
        try {
            const userRolesStr = localStorage.getItem('userRoles');
            if (!userRolesStr) return false;

            const userRoles = JSON.parse(userRolesStr);

            // Check if user has 'company_admin' role
            return Array.isArray(userRoles) && userRoles.includes('company_admin');
        } catch (error) {
            console.error('Error checking company admin status:', error);
            return false;
        }
    }

    /**
     * Get current company context
     */
    getCompanyContext() {
        try {
            if (this.isImpersonating()) {
                const adminInfo = this.getOriginalCompanyAdmin();
                return {
                    companyId: adminInfo?.company_id,
                    companyName: adminInfo?.company_name
                };
            } else {
                const userStr = localStorage.getItem('user');
                if (!userStr) return null;

                const user = JSON.parse(userStr);
                return {
                    companyId: user.company_id,
                    companyName: user.company?.name
                };
            }
        } catch (error) {
            console.error('Error getting company context:', error);
            return null;
        }
    }
}

export default new CompanyImpersonationService();
